<template>
  <div class="min-h-screen bg-gray-50 p-6 pt-24 lg:pt-8" style="direction: rtl; text-align: right;">
    <div class="max-w-7xl mx-auto">

      <!-- Header -->
      <div class="mb-6 flex items-start justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">صلاحيات أدوات المساعد الذكي</h1>
          <p class="mt-2 text-sm text-gray-600">
            تحكم في أي الأدوات يستطيع كل نوع حساب استخدامها عند التحدث مع المساعد الذكي
          </p>
        </div>
        <button
          v-if="hasChanges"
          @click="resetToDefaults"
          :disabled="saving"
          class="text-sm text-gray-500 underline hover:text-red-600 transition-colors disabled:opacity-40"
        >
          إعادة الضبط للإعدادات الافتراضية
        </button>
      </div>

      <!-- Role legend -->
      <div class="mb-6 flex flex-wrap gap-3">
        <div v-for="role in ROLES" :key="role.code" class="flex items-center gap-1.5">
          <span class="inline-block w-3 h-3 rounded-full" :style="{ background: role.color }"></span>
          <span class="text-sm text-gray-600">{{ role.label }}</span>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-16">
        <div class="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-customHafadhColor"></div>
        <p class="mt-4 text-gray-500 text-sm">جاري تحميل البيانات...</p>
      </div>

      <!-- Error -->
      <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <p class="text-red-700 text-sm">{{ error }}</p>
      </div>

      <!-- Save feedback -->
      <transition name="fade">
        <div v-if="saveMsg" class="mb-4 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-green-700 text-sm">
          {{ saveMsg }}
        </div>
      </transition>

      <!-- Permissions Table -->
      <div v-if="!loading && tools.length" class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="bg-customHafadhColor text-white">
              <th class="px-5 py-4 text-right text-sm font-semibold whitespace-nowrap min-w-[200px]">الأداة</th>
              <th
                v-for="role in ROLES"
                :key="role.code"
                class="px-4 py-4 text-center text-sm font-semibold whitespace-nowrap"
              >
                {{ role.label }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="(tool, index) in tools"
              :key="tool.tool_name"
              :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
              class="hover:bg-blue-50 transition-colors"
            >
              <!-- Tool name -->
              <td class="px-5 py-3.5">
                <div class="font-medium text-gray-800 text-sm">{{ tool.tool_name_ar }}</div>
                <div class="text-xs text-gray-400 font-mono mt-0.5 leading-none">{{ tool.tool_name }}</div>
              </td>

              <!-- Toggle per role -->
              <td
                v-for="role in ROLES"
                :key="role.code"
                class="px-4 py-3.5 text-center"
              >
                <button
                  @click="toggle(tool.tool_name, role.code)"
                  :disabled="saving || role.code === 'SUPER_ADMIN'"
                  :title="role.code === 'SUPER_ADMIN' ? 'المشرف العام لديه دائماً صلاحية كاملة' : ''"
                  class="relative inline-flex items-center justify-center w-11 h-6 rounded-full transition-all duration-200 focus:outline-none disabled:cursor-not-allowed"
                  :class="[
                    tool.roles[role.code]
                      ? 'bg-green-500 hover:bg-green-600'
                      : 'bg-gray-300 hover:bg-gray-400',
                    role.code === 'SUPER_ADMIN' ? 'opacity-60' : ''
                  ]"
                >
                  <span
                    class="inline-block w-4 h-4 bg-white rounded-full shadow transition-transform duration-200"
                    :class="tool.roles[role.code] ? '-translate-x-2.5' : 'translate-x-2.5'"
                  ></span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty state -->
      <div v-if="!loading && !tools.length" class="text-center py-16 text-gray-400 text-sm">
        لا توجد أدوات محددة بعد
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios'
import url from '@/url'

const ROLES = [
  { code: 'SUPER_ADMIN',     label: 'المشرف العام',  color: '#7c3aed' },
  { code: 'ADMIN',           label: 'الإداري',        color: '#2563eb' },
  { code: 'GENERAL_MANAGER', label: 'المدير العام',   color: '#0891b2' },
  { code: 'SUPERVISOR',      label: 'المشرف',          color: '#16a34a' },
  { code: 'TEACHER',         label: 'المعلم',          color: '#d97706' },
  { code: 'STUDENT',         label: 'الحافظ',          color: '#dc2626' },
]

export default {
  name: 'McpToolPermissionsManagement',

  data() {
    return {
      ROLES,
      tools: [],       // [{ tool_name, tool_name_ar, roles: { ROLE: bool } }]
      loading: true,
      saving: false,
      error: null,
      saveMsg: null,
      hasChanges: false,
    }
  },

  async mounted() {
    await this.load()
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        const token = localStorage.getItem('token')
        const res = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.mcp_tool_permissions,
          { headers: { 'X-Authorization': token } }
        )
        const matrix = res.data?.data ?? {}
        // Convert to flat array sorted by tool name
        this.tools = Object.entries(matrix).map(([tool_name, info]) => ({
          tool_name,
          tool_name_ar: info.tool_name_ar,
          roles: { ...info.roles },
        }))
        // Ensure SUPER_ADMIN is always true (immutable)
        this.tools.forEach(t => { t.roles['SUPER_ADMIN'] = true })
      } catch (e) {
        this.error = 'فشل تحميل الصلاحيات. تحقق من الاتصال.'
      } finally {
        this.loading = false
      }
    },

    async toggle(toolName, roleCode) {
      if (roleCode === 'SUPER_ADMIN') return

      const tool = this.tools.find(t => t.tool_name === toolName)
      if (!tool) return

      const newValue = !tool.roles[roleCode]
      tool.roles[roleCode] = newValue   // optimistic update

      this.saving = true
      this.saveMsg = null
      try {
        const token = localStorage.getItem('token')
        await axios.put(
          process.env.VUE_APP_SERVICE_URL + url.mcp_tool_permissions,
          { tool_name: toolName, role_code: roleCode, is_allowed: newValue },
          { headers: { 'X-Authorization': token } }
        )
        this.hasChanges = true
        this.showSaveMsg('تم حفظ التغيير')
      } catch (e) {
        // Revert on failure
        tool.roles[roleCode] = !newValue
        this.error = 'فشل حفظ التغيير. يرجى المحاولة مرة أخرى.'
      } finally {
        this.saving = false
      }
    },

    async resetToDefaults() {
      if (!confirm('هل أنت متأكد من إعادة جميع الصلاحيات إلى الإعدادات الافتراضية؟')) return
      this.saving = true
      try {
        const token = localStorage.getItem('token')
        await axios.delete(
          process.env.VUE_APP_SERVICE_URL + url.mcp_tool_permissions_reset,
          { headers: { 'X-Authorization': token } }
        )
        this.hasChanges = false
        this.showSaveMsg('تمت إعادة الضبط')
        await this.load()
      } catch (e) {
        this.error = 'فشل إعادة الضبط.'
      } finally {
        this.saving = false
      }
    },

    showSaveMsg(msg) {
      this.saveMsg = msg
      setTimeout(() => { this.saveMsg = null }, 3000)
    },
  },
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
